import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Modal } from '@liveauctioneers/hammer-ui-core/modal';
import {
    CloseButton,
    ModalActions,
    ModalContent,
    ModalHeader,
    ModalTitle,
} from '@liveauctioneers/hammer-ui-core/modal/components';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { modalTypes } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCertificatesHaveLoaded, getHasPendingOrCompleteCertificate } from '@/redux/modules/certCapture';
import { dismissModal, openTaxExemptModal } from '@/redux/modules/modal';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { sendResellerType } from '@/redux/modules/reseller';
import { parseQueryParams } from '@/utils/queryParams';
import { getTaxCertManagementUrl } from '@/utils/urls';

import checkForSkippablePage from './helpers/checkForSkippablePage';

const isTaxExemptPage = (path: string = '', tab: string) =>
    path.includes('dashboard/profile') && tab === 'tax-cert-management';

type QueryParams = {
    tab: string;
};

const TaxExemptModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const [show, setShow] = useState(true);
    const { formatMessage } = useIntl();
    const { tab } = parseQueryParams<QueryParams>(search);
    const taxExemptPage = isTaxExemptPage(pathname, tab);
    const isSkippablePage = checkForSkippablePage(pathname);

    const [modalPopped, setModalPopped] = useState(false);
    const isModalOpen = useAppSelector(getOpenModal) === modalTypes.TAX_EXEMPT;
    const hasValidTaxExemptCertificate = useAppSelector(getHasPendingOrCompleteCertificate);
    const certificatesHaveLoaded = useAppSelector(getCertificatesHaveLoaded);

    const automaticallyPopModal = () => {
        dispatch(openTaxExemptModal());
        setModalPopped(true);
    };

    const handleDismiss = useCallback(() => {
        setShow(false);
    }, []);

    useEffect(() => {
        if (taxExemptPage) {
            setModalPopped(true);
        }
    }, [taxExemptPage]);

    useEffect(() => {
        if (
            certificatesHaveLoaded &&
            !hasValidTaxExemptCertificate &&
            !modalPopped &&
            !taxExemptPage &&
            !isSkippablePage
        ) {
            window.setTimeout(automaticallyPopModal, 2000);
        }
    }, [certificatesHaveLoaded, hasValidTaxExemptCertificate, isSkippablePage, modalPopped, taxExemptPage]); // eslint-disable-line

    if (!isModalOpen) {
        return null;
    }

    return (
        <Modal
            data-testid="TaxExemptModal"
            handleClose={handleDismiss}
            onModalCloseAnimationEnd={() => dispatch(dismissModal())}
            show={show}
            type="composable"
        >
            <CloseButton />
            <ModalHeader>
                <ModalTitle>{formatMessage({ id: 'taxExemptModal.title' })}</ModalTitle>
            </ModalHeader>
            <ModalContent>
                <BodyPrimary className="text-center">{formatMessage({ id: 'taxExemptModal.subtitle' })}</BodyPrimary>
            </ModalContent>
            <ModalActions>
                <div className="flex flex-col items-center gap-xs">
                    <Button
                        className="w-full"
                        onClick={() => {
                            handleDismiss();
                            navigate(getTaxCertManagementUrl());
                        }}
                    >
                        {formatMessage({ id: 'taxExemptModal.upload' })}
                    </Button>
                    <Button
                        className="w-full"
                        onClick={() => {
                            dispatch(sendResellerType(0));
                            handleDismiss();
                        }}
                        variant="outlined"
                    >
                        {formatMessage({ id: 'taxExemptModal.notExempt' })}
                    </Button>
                </div>
            </ModalActions>
        </Modal>
    );
};

export default TaxExemptModal;
